import { useState } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface FilterOption {
  value: string
  label: string
}

interface FilterDropdownProps {
  label?: string
  value: string
  options: FilterOption[]
  onChange: (value: string) => void
  icon?: React.ReactNode
  className?: string
}

export function FilterDropdown({
  label,
  value,
  options,
  onChange,
  icon,
  className,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false)
  const selectedOption = options.find((opt) => opt.value === value)

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors',
            className
          )}
        >
          {icon}
          {label && <span>{label}:</span>}
          <span className="text-gray-900">{selectedOption?.label || value}</span>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-gray-400 transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto min-w-[140px] p-1 bg-white border border-gray-200 rounded-lg shadow-lg"
      >
        <div className="flex flex-col">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'flex items-center justify-between gap-2 px-3 py-2 text-sm text-left rounded-md transition-colors',
                option.value === value
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              <span>{option.label}</span>
              {option.value === value && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
